// Validates and shapes a flat RenderOp[] (the wire format from the Worker)
// into a tree the Vue host can render. Two responsibilities:
//
//   1. Each op has the right shape for its `op` discriminant (Ajv schema).
//   2. columns_open / cell_open / cell_close / columns_close and tabs_open /
//      tab_open / tab_close / tabs_close bracket correctly. Mismatched pairs
//      fail the whole render pass. Inside a `columns` block, only `cell_open`
//      and `columns_close` are accepted at the top level — leaves must live
//      inside a cell.
//
// Markdown sanitisation is not done here — the InsightText component runs
// markdown-it with html=false and pipes through DOMPurify before render.

import Ajv, { type ValidateFunction } from "ajv";

import { CHART_SPEC_SCHEMA } from "../../../shared/chart-spec-schema";
import type { LucideIconName } from "../../../shared/lucide-icons.ts";
import type {
  ActionVisibility,
  CalloutKind,
  ChartSpec,
  CheckboxOpts,
  ColumnsStack,
  DateOpts,
  DateRangeOpts,
  KpiOpts,
  MultiselectOpts,
  NumberOpts,
  NumberRangeOpts,
  RelativeDateOpts,
  RelativeDatePreset,
  RenderOp,
  SelectOption,
  SelectOpts,
  TableOpts,
  DocumentsOpts,
  TextOpts,
  ToggleOpts,
} from "./protocol";

export class InvalidRenderOps extends Error {}

// Tree shape that Vue components consume. Mirrors RenderOp leaves but
// containers (columns, tabs) carry their nested children directly.

export type TreeNode =
  | {
      kind: "kpi";
      label: string;
      value: number | string;
      opts?: KpiOpts;
      loading?: boolean;
      /**
       * Set by the worker when the underlying marker rejected. The
       * host renders `opts.error` (or a generic placeholder) in place
       * of the value. Mutually exclusive with `loading`.
       */
      errored?: boolean;
      /** Error correlation tag — appended to the banner as `[EX:<id>]`. */
      exId?: string;
    }
  | {
      kind: "text";
      markdown: string;
      loading?: boolean;
      errored?: boolean;
      exId?: string;
    }
  | {
      kind: "documents";
      rows: unknown[];
      opts?: DocumentsOpts;
      loading?: boolean;
      errored?: boolean;
      exId?: string;
    }
  | {
      kind: "table";
      rows: unknown[];
      opts?: TableOpts<unknown>;
      loading?: boolean;
      errored?: boolean;
      exId?: string;
      /**
       * Per-row action visibility, worker-evaluated from each
       * `TableAction.show` predicate. Keys are row indices as strings.
       * Absent when no action declares a predicate — the component
       * infers "every action enabled on every row".
       */
      visibleActionsById?: Record<string, ActionVisibility[]>;
    }
  | {
      kind: "chart";
      spec: ChartSpec;
      loading?: boolean;
      errored?: boolean;
      /**
       * 8-code lucent-error taxonomy (round-2 plan #2). When set, the
       * Vue host emits a `chart-error` event up to EmbedHost, which
       * forwards as a DOM `lucent-error` CustomEvent with this code.
       */
      errorCode?: string;
      exId?: string;
    }
  | {
      kind: "select";
      id: string;
      value: string | number;
      options: ReadonlyArray<SelectOption<string | number>>;
      opts?: SelectOpts;
    }
  | {
      kind: "multiselect";
      id: string;
      value: ReadonlyArray<string | number>;
      options: ReadonlyArray<SelectOption<string | number>>;
      opts?: MultiselectOpts;
    }
  | {
      kind: "checkbox_group";
      id: string;
      value: ReadonlyArray<string | number>;
      options: ReadonlyArray<SelectOption<string | number>>;
      opts?: CheckboxOpts;
    }
  | {
      kind: "toggle";
      id: string;
      value: boolean;
      opts?: ToggleOpts;
    }
  | {
      kind: "date";
      id: string;
      value: string | null;
      opts?: DateOpts;
    }
  | {
      kind: "date_range";
      id: string;
      value: readonly [string, string] | null;
      opts?: DateRangeOpts;
    }
  | {
      kind: "number";
      id: string;
      value: number | null;
      opts?: NumberOpts;
    }
  | {
      kind: "number_range";
      id: string;
      value: readonly [number, number] | null;
      opts?: NumberRangeOpts;
    }
  | {
      kind: "search";
      id: string;
      value: string;
      opts?: TextOpts;
    }
  | {
      kind: "relative_date";
      id: string;
      value: string;
      presets: ReadonlyArray<RelativeDatePreset<string>>;
      opts?: RelativeDateOpts;
    }
  | {
      kind: "columns";
      total: number;
      cells: ReadonlyArray<{ span: number; children: TreeNode[] }>;
      /**
       * Carried forward from `columns_open`. When true, the renderer
       * stretches every cell (and its leaf) to the row's tallest cell
       * — used to keep KPI rows visually uniform.
       */
      matchHeight?: boolean;
      /** Carried forward from `columns_open`; absent means "auto". */
      stack?: ColumnsStack;
    }
  | {
      kind: "tabs";
      names: string[];
      tabs: Record<string, TreeNode[]>;
      /** Optional per-tab icon name keyed by tab name. */
      icons?: Record<string, LucideIconName>;
    }
  | {
      kind: "callout";
      /** `info` / `success` / `warn` / `danger`. Renamed from the discriminator. */
      calloutKind: CalloutKind;
      title?: string;
      /** Optional curated-icon override of the kind's default glyph. */
      icon?: LucideIconName;
      children: TreeNode[];
    }
  | { kind: "divider" }
  | {
      kind: "section";
      title: string;
      caption?: string;
      description?: string;
      /** Optional curated-icon decoration next to the title. */
      icon?: LucideIconName;
      iconPosition?: "leading" | "trailing";
      children: TreeNode[];
    }
  | {
      kind: "accordion";
      summary: string;
      defaultOpen?: boolean;
      /** Optional curated-icon decoration next to the summary. */
      icon?: LucideIconName;
      iconPosition?: "leading" | "trailing";
      children: TreeNode[];
    }
  | {
      kind: "if";
      /**
       * The arm chosen by the test resolution. `"then"` until the test
       * marker resolves; flips to `"else"` if the resolved value is
       * falsy or the test rejects.
       */
      arm: "then" | "else";
      /** True until the test marker resolves; false once the arm is final. */
      loading?: boolean;
      /**
       * Set when the test marker rejected. The host renders a
       * "couldn't load" banner in place of the chosen arm; sibling
       * sections render normally. Mutually exclusive with `loading`.
       */
      errored?: boolean;
      /** Author-visible reason surfaced inside the banner. */
      errorMessage?: string;
      /** Error correlation tag — appended to the banner as `[EX:<id>]`. */
      exId?: string;
      /** Required viewer-facing footnote (`opts.whenLabel`). */
      whenLabel: string;
      /** The chosen arm's children — picked by the validator from the buffered then/else. */
      children: TreeNode[];
    }
  | {
      kind: "drilldown";
      /** Required viewer-facing footnote (`opts.whenLabel`). */
      whenLabel: string;
      /**
       * Presentation mode. `"inline"` (default) renders the body in
       * place; `"modal"` lifts it into a top-layer `<dialog>` with X /
       * Esc / backdrop close affordances.
       */
      display?: "inline" | "modal";
      /** Dialog header text. Modal-only. */
      title?: string;
      /**
       * Click-handler id (registered in the per-pass click map) that
       * the host dispatches on X / Esc / backdrop. Modal-only. Routed
       * via the `chrome-click-target` lane so the rerun-supersede
       * gate doesn't drop close clicks.
       */
      closeHandlerId?: string;
      /**
       * Set when the author's body callback threw. The host renders the
       * "couldn't load" banner in place of the children. Sibling
       * sections render normally.
       */
      errored?: boolean;
      /** Author-visible reason surfaced inside the banner. */
      errorMessage?: string;
      /** Error correlation tag — appended to the banner as `[EX:<id>]`. */
      exId?: string;
      /**
       * The body's children. Empty when the value was falsy (body
       * skipped) — the host renders just the `whenLabel` footnote.
       */
      children: TreeNode[];
    };

/**
 * Output of one render pass. `tree` is the renderable node tree; the
 * separate `formats` map is the per-field default registry built up
 * from any `sbi.format(field, format)` calls during the pass. Format
 * ops are folded into this map and stripped from the tree — they are
 * metadata, not renderable nodes.
 */
export interface RenderTree {
  tree: TreeNode[];
  formats: Record<string, string>;
}

const ajv = new Ajv({ allErrors: false, allowUnionTypes: true });

const renderOpSchema = {
  oneOf: [
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "label", "value"],
      properties: {
        op: { const: "kpi" },
        label: { type: "string" },
        value: { type: ["number", "string"] },
        loading: { type: "boolean" },
        errored: { type: "boolean" },
        // Error correlation tag. 10 uppercase hex chars; appended to
        // the user-facing banner as `[EX:<id>]`. Optional — only set
        // when the slot transitioned to `errored: true`.
        exId: { type: "string", maxLength: 32 },
        opts: {
          type: "object",
          additionalProperties: false,
          properties: {
            // DefaultOpts — see sbi.d.ts → DefaultOpts.
            format: { type: "string" },
            description: { type: "string" },
            empty: { type: "string" },
            error: { type: "string" },
            // KPI-specific.
            field: { type: "string" },
            caption: { type: "string" },
            trend: { enum: ["up", "down", "flat"] },
            // Period-over-period delta. The worker always emits the
            // resolved scalar form here — markers are stripped before
            // crossing the wire.
            delta: {
              type: "object",
              additionalProperties: false,
              required: ["value"],
              properties: {
                value: { type: ["number", "string"] },
                format: { type: "string" },
                caption: { type: "string" },
                trend: { enum: ["up", "down", "flat"] },
              },
            },
            // Sparkline series. Like `delta`, the wire format is the
            // resolved shape — `{ values: number[] }` — never a marker.
            spark: {
              type: "object",
              additionalProperties: false,
              required: ["values"],
              properties: {
                values: { type: "array", items: { type: "number" } },
              },
            },
            id: { type: "string" },
            primary: { type: "boolean" },
            // Optional decorative icon. Worker validates against the
            // curated 200-icon enum upstream; the schema only caps the
            // length so an unknown long name can't slip past save-time
            // validation as payload bloat. Same shape as the other
            // primitives that accept an `icon?` field.
            icon: { type: "string", maxLength: 64 },
            iconPosition: { enum: ["leading", "trailing"] },
          },
        },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "markdown"],
      properties: {
        op: { const: "text" },
        // 8192-char cap matches the upper bound for inline markdown
        // blocks; longer prose belongs in a separate insight or a
        // dedicated description field. Pre-existing schema gap fixed
        // alongside the sbi.ui rollout.
        markdown: { type: "string", maxLength: 8192 },
        loading: { type: "boolean" },
        errored: { type: "boolean" },
        exId: { type: "string", maxLength: 32 },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "rows"],
      properties: {
        op: { const: "documents" },
        // Rows from `sbi.doc.search` (or any rows in that shape). Capped
        // like table rows; the component reads only the documented columns.
        rows: { type: "array", maxItems: 200 },
        opts: {
          type: "object",
          additionalProperties: false,
          properties: {
            title: { type: "string", maxLength: 200 },
            description: { type: "string", maxLength: 1000 },
            sort: { enum: ["relevance", "date"] },
            limit: { type: "integer", minimum: 1, maximum: 200 },
            variant: { enum: ["full", "compact"] },
            group: { enum: ["duplicates", "excerpts", "none"] },
            item: { type: "string", maxLength: 40 },
            groupLabel: { type: "string", maxLength: 120 },
            empty: { type: "string", maxLength: 300 },
            highlight: { type: "boolean" },
            view: { type: "boolean" },
          },
        },
        loading: { type: "boolean" },
        errored: { type: "boolean" },
        exId: { type: "string", maxLength: 32 },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "rows"],
      properties: {
        op: { const: "table" },
        rows: { type: "array" },
        // `opts` is a passthrough object — DefaultOpts (format /
        // description / empty / error), columns[], maxRows, id. Shape
        // is enforced at the type level rather than here so authors
        // can extend the table opts without touching this schema.
        opts: { type: "object" },
        loading: { type: "boolean" },
        errored: { type: "boolean" },
        exId: { type: "string", maxLength: 32 },
        // Per-row action visibility: worker-computed metadata, not
        // author-supplied. Object keyed by row index (as string) with
        // an array of `{id, state, reason?}` entries. Passthrough
        // shape — exact structure enforced at the type level.
        visibleActionsById: { type: "object" },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "spec"],
      properties: {
        op: { const: "chart" },
        loading: { type: "boolean" },
        errored: { type: "boolean" },
        exId: { type: "string", maxLength: 32 },
        // Round-4 #2: per-chart errors carry a stable `errorCode` from
        // the 8-code `lucent-error` taxonomy. The host emits a typed
        // DOM `lucent-error` CustomEvent when this is set; embedders
        // brand the migration banner off `code`. Optional — most
        // chart ops don't carry one.
        errorCode: {
          enum: [
            "sbi_chart_removed",
            "chart_type_renamed",
            "unknown_chart_type",
            "chart_spec_invalid",
            "chart_data_too_large",
            "sankey_cycle_detected",
            "sunburst_depth_exceeded",
            "branch_test_failed",
          ],
        },
        spec: CHART_SPEC_SCHEMA,
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "field", "format"],
      properties: {
        op: { const: "format" },
        field: { type: "string", minLength: 1 },
        format: { type: "string", minLength: 1 },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "id", "value", "options"],
      properties: {
        op: { const: "select" },
        id: { type: "string" },
        value: { type: ["string", "number"] },
        options: {
          type: "array",
          items: {
            type: "object",
            additionalProperties: false,
            required: ["value", "label"],
            properties: {
              value: { type: ["string", "number"] },
              label: { type: "string" },
            },
          },
        },
        opts: {
          type: "object",
          additionalProperties: false,
          properties: {
            label: { type: "string" },
            description: { type: "string" },
            hideOnEmbed: { type: "boolean" },
            placeholder: { type: "string" },
          },
        },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "id", "value", "options"],
      properties: {
        op: { const: "multiselect" },
        id: { type: "string" },
        value: { type: "array", items: { type: ["string", "number"] } },
        options: {
          type: "array",
          items: {
            type: "object",
            additionalProperties: false,
            required: ["value", "label"],
            properties: {
              value: { type: ["string", "number"] },
              label: { type: "string" },
            },
          },
        },
        opts: {
          type: "object",
          additionalProperties: false,
          properties: {
            label: { type: "string" },
            description: { type: "string" },
            hideOnEmbed: { type: "boolean" },
            placeholder: { type: "string" },
          },
        },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "id", "value", "options"],
      properties: {
        op: { const: "checkbox_group" },
        id: { type: "string" },
        value: { type: "array", items: { type: ["string", "number"] } },
        options: {
          type: "array",
          items: {
            type: "object",
            additionalProperties: false,
            required: ["value", "label"],
            properties: {
              value: { type: ["string", "number"] },
              label: { type: "string" },
            },
          },
        },
        opts: {
          type: "object",
          additionalProperties: false,
          properties: {
            label: { type: "string" },
            description: { type: "string" },
            hideOnEmbed: { type: "boolean" },
            layout: { enum: ["horizontal", "vertical"] },
          },
        },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "id", "value"],
      properties: {
        op: { const: "toggle" },
        id: { type: "string" },
        value: { type: "boolean" },
        opts: {
          type: "object",
          additionalProperties: false,
          properties: {
            label: { type: "string" },
            description: { type: "string" },
            hideOnEmbed: { type: "boolean" },
            onLabel: { type: "string" },
            offLabel: { type: "string" },
          },
        },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "id", "value"],
      properties: {
        op: { const: "date" },
        id: { type: "string" },
        value: { type: ["string", "null"] },
        opts: {
          type: "object",
          additionalProperties: false,
          properties: {
            label: { type: "string" },
            description: { type: "string" },
            hideOnEmbed: { type: "boolean" },
            min: { type: "string" },
            max: { type: "string" },
          },
        },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "id", "value"],
      properties: {
        op: { const: "date_range" },
        id: { type: "string" },
        value: {
          oneOf: [
            { type: "null" },
            {
              type: "array",
              items: { type: "string" },
              minItems: 2,
              maxItems: 2,
            },
          ],
        },
        opts: {
          type: "object",
          additionalProperties: false,
          properties: {
            label: { type: "string" },
            description: { type: "string" },
            hideOnEmbed: { type: "boolean" },
            min: { type: "string" },
            max: { type: "string" },
          },
        },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "id", "value"],
      properties: {
        op: { const: "number" },
        id: { type: "string" },
        value: { type: ["number", "null"] },
        opts: {
          type: "object",
          additionalProperties: false,
          properties: {
            label: { type: "string" },
            description: { type: "string" },
            hideOnEmbed: { type: "boolean" },
            min: { type: "number" },
            max: { type: "number" },
            step: { type: "number" },
            placeholder: { type: "string" },
          },
        },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "id", "value"],
      properties: {
        op: { const: "number_range" },
        id: { type: "string" },
        value: {
          oneOf: [
            { type: "null" },
            {
              type: "array",
              items: { type: "number" },
              minItems: 2,
              maxItems: 2,
            },
          ],
        },
        opts: {
          type: "object",
          additionalProperties: false,
          properties: {
            label: { type: "string" },
            description: { type: "string" },
            hideOnEmbed: { type: "boolean" },
            min: { type: "number" },
            max: { type: "number" },
            step: { type: "number" },
          },
        },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "id", "value"],
      properties: {
        op: { const: "search" },
        id: { type: "string" },
        value: { type: "string" },
        opts: {
          type: "object",
          additionalProperties: false,
          properties: {
            label: { type: "string" },
            description: { type: "string" },
            hideOnEmbed: { type: "boolean" },
            placeholder: { type: "string" },
            debounceMs: { type: "number", minimum: 0 },
          },
        },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "id", "value", "presets"],
      properties: {
        op: { const: "relative_date" },
        id: { type: "string" },
        value: { type: "string" },
        presets: {
          type: "array",
          items: {
            type: "object",
            additionalProperties: false,
            required: ["value", "label"],
            properties: {
              value: { type: "string" },
              label: { type: "string" },
            },
          },
          // No `minItems`: presets may be built from a query that
          // legitimately returns zero rows, exactly as `select` /
          // `multiselect` / `checkbox_group` options may. Those three
          // were relaxed to allow empty dynamic lists; this one was
          // missed, so an empty preset list hard-failed the render
          // instead of showing the host's empty state.
        },
        opts: {
          type: "object",
          additionalProperties: false,
          properties: {
            label: { type: "string" },
            description: { type: "string" },
            hideOnEmbed: { type: "boolean" },
          },
        },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "total"],
      properties: {
        op: { const: "columns_open" },
        total: { type: "integer", minimum: 1 },
        matchHeight: { type: "boolean" },
        stack: { enum: ["auto", "compact", "never"] },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "span"],
      properties: {
        op: { const: "cell_open" },
        // The runtime clamps to 1–12 before emitting; the cap here is
        // defence in depth, matching the renderer's 12-track grid.
        span: { type: "integer", minimum: 1, maximum: 12 },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op"],
      properties: { op: { const: "cell_close" } },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op"],
      properties: { op: { const: "columns_close" } },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "names"],
      properties: {
        op: { const: "tabs_open" },
        names: {
          type: "array",
          items: { type: "string" },
          minItems: 1,
        },
        // Optional per-tab icon map keyed by tab name. Each value is
        // an icon name from the curated 200-icon enum; the worker
        // validates the enum membership upstream so the wire schema
        // only caps length.
        icons: {
          type: "object",
          additionalProperties: { type: "string", maxLength: 64 },
        },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "name"],
      properties: { op: { const: "tab_open" }, name: { type: "string" } },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op"],
      properties: { op: { const: "tab_close" } },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op"],
      properties: { op: { const: "tabs_close" } },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "arm", "whenLabel"],
      properties: {
        op: { const: "if_open" },
        arm: { type: "string", enum: ["then", "else"] },
        loading: { type: "boolean" },
        errored: { type: "boolean" },
        // maxLength caps prevent log-injection / DoS via oversized author
        // strings (security DiD): a 10MB whenLabel survives schema check
        // without one and lands in DOM-key paths and audit logs.
        errorMessage: { type: "string", maxLength: 1024 },
        exId: { type: "string", maxLength: 32 },
        whenLabel: { type: "string", maxLength: 256 },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op"],
      properties: { op: { const: "else_open" } },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op"],
      properties: { op: { const: "if_close" } },
    },
    // ── sbi.drilldown — state-gated detail region ───────────────────────
    //
    // Wrapper pair emitted around the body of `sbi.drilldown(value, body,
    // opts)`. When the value is falsy the body runs no children, but the
    // pair is still emitted so the host has a stable region (and the
    // `whenLabel` footnote always renders). The `errored` / `errorMessage`
    // fields appear when the body callback throws — the runtime catches
    // it inside the per-body try/catch and patches the open frame so the
    // host renders the "couldn't load" banner instead of partial children.
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "whenLabel"],
      properties: {
        op: { const: "drilldown_open" },
        // Same caps as branch's whenLabel (256 / 1024) so authoring
        // limits and audit-log shapes line up across the two primitives.
        whenLabel: { type: "string", maxLength: 256 },
        // Presentation mode. Absent → host treats as "inline" (legacy
        // payloads from before modal support). Enum (not type: string)
        // so a crafted value can't flow into a CSS class-name template.
        display: { enum: ["inline", "modal"] },
        // Modal header text. Same maxLength as `callout.title` —
        // viewer-facing, short by convention.
        title: { type: "string", maxLength: 200 },
        // Click-handler id for X / Esc / backdrop dispatch. The worker
        // mints these as `__drilldown_close_N`; the schema accepts any
        // bounded string so test fixtures with bare ids still pass.
        closeHandlerId: { type: "string", maxLength: 128 },
        errored: { type: "boolean" },
        errorMessage: { type: "string", maxLength: 1024 },
        exId: { type: "string", maxLength: 32 },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op"],
      properties: { op: { const: "drilldown_close" } },
    },
    // ── sbi.ui — callout / divider / section / accordion ─────────────────
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "kind"],
      properties: {
        op: { const: "callout_open" },
        // Enum (not type: string) so a crafted `kind: "__proto__"` can't
        // flow into a CSS class-name template like `callout-${kind}`.
        kind: { enum: ["info", "success", "warn", "danger"] },
        // maxLength cap prevents log-injection / DoS via oversized title.
        title: { type: "string", maxLength: 200 },
        // Optional icon name from the curated 200-icon set. Cap length;
        // the worker enforces the closed enum upstream.
        icon: { type: "string", maxLength: 64 },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op"],
      properties: { op: { const: "callout_close" } },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op"],
      properties: { op: { const: "divider" } },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "title"],
      properties: {
        op: { const: "section_open" },
        title: { type: "string", maxLength: 200 },
        caption: { type: "string", maxLength: 200 },
        description: { type: "string", maxLength: 2000 },
        icon: { type: "string", maxLength: 64 },
        iconPosition: { enum: ["leading", "trailing"] },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op"],
      properties: { op: { const: "section_close" } },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "summary"],
      properties: {
        op: { const: "accordion_open" },
        summary: { type: "string", maxLength: 200 },
        defaultOpen: { type: "boolean" },
        icon: { type: "string", maxLength: 64 },
        iconPosition: { enum: ["leading", "trailing"] },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op"],
      properties: { op: { const: "accordion_close" } },
    },
  ],
};

const validateOp = ajv.compile(renderOpSchema) as ValidateFunction<RenderOp>;

// Each `oneOf` arm is discriminated by `op: { const: "<name>" }`. Build a
// reverse map at module init so we can narrow AJV errors to the *matching*
// arm — AJV's default behaviour is to report the first failure from every
// arm, which produces a cascade like:
//
//   op[9] failed schema: data/opts must NOT have additional properties,
//   data must have required property 'markdown',
//   data must have required property 'rows',
//   data must have required property 'spec', …
//
// The first line is the truth; everything after is `oneOf` cycling
// through arms that never matched the discriminator. With this map we
// can look up the arm AJV *should* have validated against (by the op's
// own `op` field) and keep only that arm's errors, plus any top-level
// errors that didn't come from a specific arm.
const OP_TO_ARM_INDEX: ReadonlyMap<string, number> = (() => {
  const arms = (renderOpSchema as { oneOf: ReadonlyArray<Record<string, unknown>> })
    .oneOf;
  const map = new Map<string, number>();
  for (let i = 0; i < arms.length; i++) {
    const props = (arms[i] as { properties?: Record<string, unknown> }).properties;
    const opSchema = props?.op as { const?: unknown } | undefined;
    if (opSchema && typeof opSchema.const === "string") {
      map.set(opSchema.const, i);
    }
  }
  return map;
})();

type AjvErrorObject = NonNullable<typeof validateOp.errors>[number];

/**
 * Turn a single AJV error into a self-explanatory sentence.
 *
 * AJV's default `errorsText` formatting reads as cryptic field-rule
 * pairs ("data/opts must NOT have additional properties") — the word
 * "data" is its generic placeholder for the value being validated,
 * and the offending property name is buried inside `err.params`. This
 * formatter splices `params` back into the message and replaces "data"
 * with the op kind so the line reads as it would in a code review:
 *
 *   kpi.opts has unknown property `icon` (allowed: format, description,
 *     empty, error, field, caption, trend, …)
 *   table is missing required property `rows`
 *   section_open.title must be a string
 *   kpi.opts.iconPosition must be one of: leading, trailing
 *
 * `instancePath` is the JSON-Pointer-ish path AJV records onto the
 * input data ("/opts/icon"); we render it as a dotted path under the
 * op kind for readability.
 */
function formatAjvError(opName: string, err: AjvErrorObject): string {
  const path = (err.instancePath ?? "").replace(/^\//, "").replace(/\//g, ".");
  const subject = path ? `${opName}.${path}` : opName;
  const params = (err.params ?? {}) as Record<string, unknown>;
  switch (err.keyword) {
    case "additionalProperties": {
      const extra = params.additionalProperty;
      return `${subject} has unknown property \`${String(extra)}\``;
    }
    case "required": {
      const missing = params.missingProperty;
      return `${subject} is missing required property \`${String(missing)}\``;
    }
    case "type": {
      const expected = params.type;
      return `${subject} must be ${Array.isArray(expected) ? expected.join(" or ") : String(expected)}`;
    }
    case "enum": {
      const allowed = params.allowedValues;
      return `${subject} must be one of: ${Array.isArray(allowed) ? allowed.join(", ") : String(allowed)}`;
    }
    case "const": {
      const expected = params.allowedValue;
      return `${subject} must equal \`${String(expected)}\``;
    }
    case "maxLength":
    case "minLength": {
      const limit = params.limit;
      const adj = err.keyword === "maxLength" ? "longer than" : "shorter than";
      return `${subject} is ${adj} the allowed length (${String(limit)} chars)`;
    }
    case "maximum":
    case "minimum":
    case "exclusiveMaximum":
    case "exclusiveMinimum": {
      const limit = params.limit;
      const cmp =
        err.keyword === "maximum"
          ? "≤"
          : err.keyword === "minimum"
          ? "≥"
          : err.keyword === "exclusiveMaximum"
          ? "<"
          : ">";
      return `${subject} must be ${cmp} ${String(limit)}`;
    }
    case "pattern": {
      const pattern = params.pattern;
      return `${subject} doesn't match the required pattern (${String(pattern)})`;
    }
    case "minItems":
    case "maxItems": {
      const limit = params.limit;
      const adj = err.keyword === "maxItems" ? "more than" : "fewer than";
      return `${subject} has ${adj} ${String(limit)} entries`;
    }
    default:
      // Fall back to AJV's own message so we don't drop information
      // for keywords we haven't enumerated.
      return `${subject} ${err.message ?? `failed ${err.keyword}`}`;
  }
}

/**
 * Format AJV errors for a single render op. When the op's `op` field
 * matches a known arm, restrict the reported errors to that arm so the
 * author sees the actual mismatch instead of a wall of unrelated
 * `required property` complaints from every other arm. Each surviving
 * error is rendered as a self-explanatory sentence (see `formatAjvError`)
 * naming the op kind, the property path, and the offending value. The
 * terminal `oneOf` summary is dropped — it doesn't add information once
 * we've narrowed.
 *
 * Unknown / missing `op` values fall back to AJV's default text so we
 * never *hide* a problem we don't understand.
 */
function formatRenderOpErrors(
  op: unknown,
  errors: ReadonlyArray<AjvErrorObject>,
): string {
  const opName =
    op && typeof op === "object"
      ? (op as { op?: unknown }).op
      : undefined;
  const armIndex =
    typeof opName === "string" ? OP_TO_ARM_INDEX.get(opName) : undefined;
  if (armIndex === undefined || typeof opName !== "string") {
    return ajv.errorsText(errors as typeof validateOp.errors);
  }
  const armPrefix = `#/oneOf/${armIndex}/`;
  const relevant = errors.filter((err) => {
    if (err.keyword === "oneOf") return false; // summary noise
    const path = err.schemaPath ?? "";
    if (path.startsWith("#/oneOf/")) return path.startsWith(armPrefix);
    return true; // top-level errors (e.g. "required: op") kept as-is
  });
  if (relevant.length === 0) {
    // Defensive — shouldn't happen, but fall back rather than emit
    // an empty string.
    return ajv.errorsText(errors as typeof validateOp.errors);
  }
  return relevant.map((err) => formatAjvError(opName, err)).join("; ");
}

// ── Tree construction ──────────────────────────────────────────────────────

type Frame =
  | { kind: "root"; outputs: TreeNode[] }
  | {
      kind: "columns";
      total: number;
      cells: { span: number; children: TreeNode[] }[];
      matchHeight?: boolean;
      stack?: ColumnsStack;
    }
  | { kind: "cell"; span: number; outputs: TreeNode[] }
  | {
      kind: "tabs";
      names: string[];
      tabs: Record<string, TreeNode[]>;
      icons?: Record<string, LucideIconName>;
    }
  | { kind: "tab"; outputs: TreeNode[] }
  | {
      kind: "callout";
      calloutKind: CalloutKind;
      title?: string;
      icon?: LucideIconName;
      outputs: TreeNode[];
    }
  | {
      kind: "section";
      title: string;
      caption?: string;
      description?: string;
      icon?: LucideIconName;
      iconPosition?: "leading" | "trailing";
      outputs: TreeNode[];
    }
  | {
      kind: "accordion";
      summary: string;
      defaultOpen?: boolean;
      icon?: LucideIconName;
      iconPosition?: "leading" | "trailing";
      outputs: TreeNode[];
    }
  | {
      kind: "if";
      arm: "then" | "else";
      loading?: boolean;
      errored?: boolean;
      errorMessage?: string;
      exId?: string;
      whenLabel: string;
      thenChildren: TreeNode[];
      elseChildren: TreeNode[];
      /**
       * Which arm we're currently buffering ops into. Starts at `"then"`;
       * flips to `"else"` on `else_open`. Independent of `arm` (which is
       * the *chosen* arm picked by Phase-2 patching). The validator drops
       * the un-chosen arm's children when shaping the TreeNode.
       */
      writingTo: "then" | "else";
    }
  | {
      kind: "drilldown";
      whenLabel: string;
      // Carried through from `drilldown_open` so the closing
      // `pushLeaf` can copy them onto the emitted TreeNode.
      display?: "inline" | "modal";
      title?: string;
      closeHandlerId?: string;
      errored?: boolean;
      errorMessage?: string;
      exId?: string;
      outputs: TreeNode[];
    };

function pushLeaf(stack: Frame[], node: TreeNode): void {
  const top = stack[stack.length - 1];
  if (top.kind === "tabs") {
    throw new InvalidRenderOps(
      "render content outside a tab (must be inside tab_open/tab_close)",
    );
  }
  if (top.kind === "columns") {
    throw new InvalidRenderOps(
      "render content outside a cell (must be inside cell_open/cell_close)",
    );
  }
  if (top.kind === "if") {
    if (top.writingTo === "then") top.thenChildren.push(node);
    else top.elseChildren.push(node);
    return;
  }
  // Drilldown frames push directly onto their outputs — single-arm,
  // no `else` branch to disambiguate. When the body was gated off
  // (falsy value) the worker simply emits no children between open
  // and close.
  top.outputs.push(node);
}

export function validateAndParse(ops: unknown[]): RenderTree {
  for (let i = 0; i < ops.length; i++) {
    if (!validateOp(ops[i])) {
      const detail = formatRenderOpErrors(ops[i], validateOp.errors ?? []);
      throw new InvalidRenderOps(`op[${i}] failed schema: ${detail}`);
    }
  }
  const validated = ops as RenderOp[];

  const stack: Frame[] = [{ kind: "root", outputs: [] }];
  // Field → format string registrations are collected outside the
  // tree so they apply uniformly regardless of where in the render
  // stream the author called `sbi.format`. Last write wins.
  const formats: Record<string, string> = {};

  for (const op of validated) {
    const top = stack[stack.length - 1];
    switch (op.op) {
      case "kpi":
        pushLeaf(stack, {
          kind: "kpi",
          label: op.label,
          value: op.value,
          opts: op.opts,
          ...(op.loading !== undefined ? { loading: op.loading } : {}),
          ...(op.errored !== undefined ? { errored: op.errored } : {}),
          ...((op as { exId?: string }).exId !== undefined
            ? { exId: (op as { exId?: string }).exId }
            : {}),
        });
        break;
      case "text":
        pushLeaf(stack, {
          kind: "text",
          markdown: op.markdown,
          ...(op.loading !== undefined ? { loading: op.loading } : {}),
          ...(op.errored !== undefined ? { errored: op.errored } : {}),
          ...((op as { exId?: string }).exId !== undefined
            ? { exId: (op as { exId?: string }).exId }
            : {}),
        });
        break;
      case "documents":
        pushLeaf(stack, {
          kind: "documents",
          rows: op.rows,
          ...(op.opts !== undefined ? { opts: op.opts } : {}),
          ...(op.loading !== undefined ? { loading: op.loading } : {}),
          ...(op.errored !== undefined ? { errored: op.errored } : {}),
          ...((op as { exId?: string }).exId !== undefined
            ? { exId: (op as { exId?: string }).exId }
            : {}),
        });
        break;
      case "table":
        pushLeaf(stack, {
          kind: "table",
          rows: op.rows,
          opts: op.opts,
          ...(op.loading !== undefined ? { loading: op.loading } : {}),
          ...(op.errored !== undefined ? { errored: op.errored } : {}),
          ...((op as { exId?: string }).exId !== undefined
            ? { exId: (op as { exId?: string }).exId }
            : {}),
          ...((op as { visibleActionsById?: Record<string, ActionVisibility[]> })
            .visibleActionsById !== undefined
            ? {
                visibleActionsById: (
                  op as { visibleActionsById?: Record<string, ActionVisibility[]> }
                ).visibleActionsById,
              }
            : {}),
        });
        break;
      case "chart":
        pushLeaf(stack, {
          kind: "chart",
          spec: op.spec,
          ...(op.loading !== undefined ? { loading: op.loading } : {}),
          ...(op.errored !== undefined ? { errored: op.errored } : {}),
          ...((op as { errorCode?: string }).errorCode !== undefined
            ? { errorCode: (op as { errorCode?: string }).errorCode }
            : {}),
          ...((op as { exId?: string }).exId !== undefined
            ? { exId: (op as { exId?: string }).exId }
            : {}),
        });
        break;
      case "format":
        // Format declarations are sideband — they don't produce a
        // tree node, they just update the per-pass registry.
        formats[op.field] = op.format;
        break;
      case "select":
        pushLeaf(stack, {
          kind: "select",
          id: op.id,
          value: op.value,
          options: op.options,
          opts: op.opts,
        });
        break;
      case "multiselect":
        pushLeaf(stack, {
          kind: "multiselect",
          id: op.id,
          value: op.value,
          options: op.options,
          opts: op.opts,
        });
        break;
      case "checkbox_group":
        pushLeaf(stack, {
          kind: "checkbox_group",
          id: op.id,
          value: op.value,
          options: op.options,
          opts: op.opts,
        });
        break;
      case "toggle":
        pushLeaf(stack, {
          kind: "toggle",
          id: op.id,
          value: op.value,
          opts: op.opts,
        });
        break;
      case "date":
        pushLeaf(stack, {
          kind: "date",
          id: op.id,
          value: op.value,
          opts: op.opts,
        });
        break;
      case "date_range":
        pushLeaf(stack, {
          kind: "date_range",
          id: op.id,
          value: op.value,
          opts: op.opts,
        });
        break;
      case "number":
        pushLeaf(stack, {
          kind: "number",
          id: op.id,
          value: op.value,
          opts: op.opts,
        });
        break;
      case "number_range":
        pushLeaf(stack, {
          kind: "number_range",
          id: op.id,
          value: op.value,
          opts: op.opts,
        });
        break;
      case "search":
        pushLeaf(stack, {
          kind: "search",
          id: op.id,
          value: op.value,
          opts: op.opts,
        });
        break;
      case "relative_date":
        pushLeaf(stack, {
          kind: "relative_date",
          id: op.id,
          value: op.value,
          presets: op.presets,
          opts: op.opts,
        });
        break;

      case "columns_open":
        stack.push({
          kind: "columns",
          total: op.total,
          cells: [],
          ...(op.matchHeight ? { matchHeight: true } : {}),
          ...(op.stack && op.stack !== "auto" ? { stack: op.stack } : {}),
        });
        break;
      case "cell_open": {
        if (top.kind !== "columns") {
          throw new InvalidRenderOps("cell_open outside columns_open");
        }
        stack.push({ kind: "cell", span: op.span, outputs: [] });
        break;
      }
      case "cell_close": {
        if (top.kind !== "cell") {
          throw new InvalidRenderOps("cell_close without matching cell_open");
        }
        stack.pop();
        const parent = stack[stack.length - 1];
        if (parent.kind !== "columns") {
          throw new InvalidRenderOps("internal: cell parent is not columns");
        }
        parent.cells.push({ span: top.span, children: top.outputs });
        break;
      }
      case "columns_close": {
        if (top.kind !== "columns") {
          throw new InvalidRenderOps("columns_close without matching columns_open");
        }
        stack.pop();
        pushLeaf(stack, {
          kind: "columns",
          total: top.total,
          cells: top.cells,
          ...(top.matchHeight ? { matchHeight: true } : {}),
          ...(top.stack ? { stack: top.stack } : {}),
        });
        break;
      }

      case "tabs_open": {
        const tabs: Record<string, TreeNode[]> = {};
        for (const n of op.names) tabs[n] = [];
        stack.push({
          kind: "tabs",
          names: [...op.names],
          tabs,
          ...(op.icons ? { icons: { ...op.icons } as Record<string, LucideIconName> } : {}),
        });
        break;
      }
      case "tab_open": {
        if (top.kind !== "tabs") {
          throw new InvalidRenderOps("tab_open outside tabs_open");
        }
        if (!(op.name in top.tabs)) {
          throw new InvalidRenderOps(`tab_open with unknown name '${op.name}'`);
        }
        stack.push({ kind: "tab", outputs: top.tabs[op.name] });
        break;
      }
      case "tab_close": {
        if (top.kind !== "tab") {
          throw new InvalidRenderOps("tab_close outside tab_open");
        }
        stack.pop();
        break;
      }
      case "tabs_close": {
        if (top.kind !== "tabs") {
          throw new InvalidRenderOps("tabs_close without matching tabs_open");
        }
        stack.pop();
        pushLeaf(stack, {
          kind: "tabs",
          ...(top.icons ? { icons: top.icons } : {}),
          names: top.names,
          tabs: top.tabs,
        });
        break;
      }

      case "callout_open":
        stack.push({
          kind: "callout",
          calloutKind: op.kind,
          ...(op.title !== undefined ? { title: op.title } : {}),
          ...(op.icon !== undefined ? { icon: op.icon as LucideIconName } : {}),
          outputs: [],
        });
        break;
      case "callout_close": {
        if (top.kind !== "callout") {
          throw new InvalidRenderOps(
            "callout_close without matching callout_open",
          );
        }
        stack.pop();
        pushLeaf(stack, {
          kind: "callout",
          calloutKind: top.calloutKind,
          ...(top.title !== undefined ? { title: top.title } : {}),
          ...(top.icon !== undefined ? { icon: top.icon } : {}),
          children: top.outputs,
        });
        break;
      }
      case "divider":
        pushLeaf(stack, { kind: "divider" });
        break;
      case "section_open":
        stack.push({
          kind: "section",
          title: op.title,
          ...(op.caption !== undefined ? { caption: op.caption } : {}),
          ...(op.description !== undefined ? { description: op.description } : {}),
          ...(op.icon !== undefined ? { icon: op.icon as LucideIconName } : {}),
          ...(op.iconPosition !== undefined
            ? { iconPosition: op.iconPosition as "leading" | "trailing" }
            : {}),
          outputs: [],
        });
        break;
      case "section_close": {
        if (top.kind !== "section") {
          throw new InvalidRenderOps(
            "section_close without matching section_open",
          );
        }
        stack.pop();
        pushLeaf(stack, {
          kind: "section",
          title: top.title,
          ...(top.caption !== undefined ? { caption: top.caption } : {}),
          ...(top.description !== undefined ? { description: top.description } : {}),
          ...(top.icon !== undefined ? { icon: top.icon } : {}),
          ...(top.iconPosition !== undefined ? { iconPosition: top.iconPosition } : {}),
          children: top.outputs,
        });
        break;
      }
      case "accordion_open":
        stack.push({
          kind: "accordion",
          summary: op.summary,
          ...(op.defaultOpen !== undefined ? { defaultOpen: op.defaultOpen } : {}),
          ...(op.icon !== undefined ? { icon: op.icon as LucideIconName } : {}),
          ...(op.iconPosition !== undefined
            ? { iconPosition: op.iconPosition as "leading" | "trailing" }
            : {}),
          outputs: [],
        });
        break;
      case "accordion_close": {
        if (top.kind !== "accordion") {
          throw new InvalidRenderOps(
            "accordion_close without matching accordion_open",
          );
        }
        stack.pop();
        pushLeaf(stack, {
          kind: "accordion",
          summary: top.summary,
          ...(top.defaultOpen !== undefined ? { defaultOpen: top.defaultOpen } : {}),
          ...(top.icon !== undefined ? { icon: top.icon } : {}),
          ...(top.iconPosition !== undefined ? { iconPosition: top.iconPosition } : {}),
          children: top.outputs,
        });
        break;
      }

      case "if_open":
        stack.push({
          kind: "if",
          arm: op.arm,
          ...(op.loading !== undefined ? { loading: op.loading } : {}),
          ...(op.errored !== undefined ? { errored: op.errored } : {}),
          ...(op.errorMessage !== undefined ? { errorMessage: op.errorMessage } : {}),
          ...((op as { exId?: string }).exId !== undefined
            ? { exId: (op as { exId?: string }).exId }
            : {}),
          whenLabel: op.whenLabel,
          thenChildren: [],
          elseChildren: [],
          writingTo: "then",
        });
        break;
      case "else_open": {
        if (top.kind !== "if") {
          throw new InvalidRenderOps("else_open outside if_open");
        }
        if (top.writingTo === "else") {
          throw new InvalidRenderOps("multiple else_open in single if_open frame");
        }
        top.writingTo = "else";
        break;
      }
      case "if_close": {
        if (top.kind !== "if") {
          throw new InvalidRenderOps("if_close without matching if_open");
        }
        stack.pop();
        pushLeaf(stack, {
          kind: "if",
          arm: top.arm,
          ...(top.loading !== undefined ? { loading: top.loading } : {}),
          ...(top.errored !== undefined ? { errored: top.errored } : {}),
          ...(top.errorMessage !== undefined ? { errorMessage: top.errorMessage } : {}),
          ...(top.exId !== undefined ? { exId: top.exId } : {}),
          whenLabel: top.whenLabel,
          // Drop the un-chosen arm's children from the rendered tree.
          // When the test rejected, `arm` was set to `"else"` and `errored`
          // is true — the host renders the banner instead of the children,
          // but we still pass them through (for v2 fallback flexibility).
          children: top.arm === "then" ? top.thenChildren : top.elseChildren,
        });
        break;
      }

      case "drilldown_open":
        stack.push({
          kind: "drilldown",
          whenLabel: op.whenLabel,
          ...(op.display !== undefined ? { display: op.display } : {}),
          ...(op.title !== undefined ? { title: op.title } : {}),
          ...(op.closeHandlerId !== undefined
            ? { closeHandlerId: op.closeHandlerId }
            : {}),
          ...(op.errored !== undefined ? { errored: op.errored } : {}),
          ...(op.errorMessage !== undefined ? { errorMessage: op.errorMessage } : {}),
          ...((op as { exId?: string }).exId !== undefined
            ? { exId: (op as { exId?: string }).exId }
            : {}),
          outputs: [],
        });
        break;
      case "drilldown_close": {
        if (top.kind !== "drilldown") {
          throw new InvalidRenderOps(
            "drilldown_close without matching drilldown_open",
          );
        }
        stack.pop();
        pushLeaf(stack, {
          kind: "drilldown",
          whenLabel: top.whenLabel,
          ...(top.display !== undefined ? { display: top.display } : {}),
          ...(top.title !== undefined ? { title: top.title } : {}),
          ...(top.closeHandlerId !== undefined
            ? { closeHandlerId: top.closeHandlerId }
            : {}),
          ...(top.errored !== undefined ? { errored: top.errored } : {}),
          ...(top.errorMessage !== undefined ? { errorMessage: top.errorMessage } : {}),
          ...(top.exId !== undefined ? { exId: top.exId } : {}),
          children: top.outputs,
        });
        break;
      }
    }
  }

  if (stack.length !== 1) {
    // Name the still-open container kinds so authors / debuggers see
    // exactly which `*_open` was never matched. The top-of-stack
    // frame is the most informative — that's where the leak started.
    const top = stack[stack.length - 1];
    throw new InvalidRenderOps(
      `unclosed ${top.kind} at end of render`,
    );
  }
  const root = stack[0];
  if (root.kind !== "root") {
    throw new InvalidRenderOps("internal: stack did not unwind to root");
  }
  return { tree: root.outputs, formats };
}
